/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import type { RefObject } from 'preact';
import { useRef } from 'preact/hooks';
import type { MutableRef } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import { getValue, getDimensions } from './ratingUtils';
import { CommitDetail, InputDetail } from '#utils/UNSAFE_meterUtils/meterTypes';
import { useKeyboardEvents } from '#hooks/PRIVATE_useVisMeterEvents';

export const useEvents = (
  max: number,
  step: number,
  value: number,
  rootRef: RefObject<HTMLElement>,
  dimensionsRef: MutableRef<{ width: number; x: number }>,
  isInteractive: boolean,
  onCommit?: (detail: CommitDetail) => void,
  onInput?: (detail: InputDetail) => void
) => {
  const currentInputRef = useRef<number>();
  const { direction } = useUser();

  const isRtl = direction === 'rtl';

  const handleInput = (inputValue: number | undefined) => {
    onInput?.({ value: inputValue } as InputDetail);
    currentInputRef.current = inputValue;
  };

  const pointerUpHandler = (event: PointerEvent) => {
    const val = getValue(event.pageX, max, step, dimensionsRef, isRtl);
    if (val !== -1) {
      onCommit?.({ value: val } as CommitDetail);
    }
  };

  // When the document or rating gauge parent scrolls, we need to update the dimensionRef.current.x.
  // Instead of updating it for every scroll event, recalculate the dims the first time mouse enters
  // so we get fresh dimension value.
  const pointerEnterHandler = () => {
    dimensionsRef.current = getDimensions(rootRef.current);
  };

  const hoverHandler = (event: PointerEvent) => {
    const val = getValue(event.pageX, max, step, dimensionsRef, isRtl);
    if (val !== -1 && val != currentInputRef.current) {
      handleInput(val);
    }
  };

  const blurHandler = () => {
    onCommit?.({ value: value } as CommitDetail);
  };

  const pointerLeaveHandler = () => {
    handleInput(undefined);
  };

  const keyboardEventsProps = useKeyboardEvents(
    value,
    0,
    max,
    step,
    currentInputRef,
    onCommit,
    onInput
  );

  return isInteractive
    ? {
        onPointerUp: pointerUpHandler,
        onBlur: blurHandler,
        onPointerMove: hoverHandler,
        onKeyUp: keyboardEventsProps.onKeyUp,
        onKeyDown: keyboardEventsProps.onKeyDown,
        onPointerLeave: pointerLeaveHandler,
        onPointerEnter: pointerEnterHandler
      }
    : {};
};
